import { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { siretProblem } from '../../utils/siret';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';
import { nullIfBlank } from './useMyListing';

/**
 * Whether the listing asks who the business is. A private individual has no
 * legal identity to give, and the API does not ask them for one either.
 */
export function claimNeedsIdentity(provider) {
  return 'INDIVIDUAL' !== provider?.legal_status;
}

/**
 * The account's request to take over a listing an admin curated: read it,
 * send it, take it back.
 *
 * `claim` is the latest one, whatever became of it — pending, refused with a
 * reason, approved, withdrawn — or null for the account that never asked.
 * `enabled: false` is for a visitor, who has no claim to read and would only
 * be told 401.
 */
export function useMyClaim({ enabled = true } = {}) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    const load = async () => {
      try {
        const response = await apiCall('/me/claim', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!cancelled && response.ok) {
          setClaim(data.data ?? null);
        }
      } catch {
        // Not knowing about a claim only means the page offers to make one;
        // the API refuses a second one if there is.
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const reset = useCallback(() => {
    setError('');
    setFieldErrors({});
  }, []);

  /**
   * Ask for the listing behind `provider`. Returns whether the request left.
   */
  const send = async (provider, { legalName, siret, message }) => {
    reset();

    const asksIdentity = claimNeedsIdentity(provider);

    // Before the request, as on the listing form: a mistyped digit belongs
    // under the field, not in an alert after a round trip.
    if (asksIdentity) {
      const problems = {};

      if (!nullIfBlank(legalName)) {
        problems.legalName = 'Indiquez la raison sociale de l’entreprise';
      }

      problems.siret = nullIfBlank(siret) ? siretProblem(siret) : 'Indiquez le SIRET de l’entreprise';

      if (!problems.siret) {
        delete problems.siret;
      }

      if (Object.keys(problems).length > 0) {
        setFieldErrors(problems);
        return false;
      }
    }

    setSaving(true);

    try {
      const response = await apiCall(`/providers/${provider.slug}/claim`, {
        method: 'POST',
        body: JSON.stringify({
          legalName: asksIdentity ? nullIfBlank(legalName) : null,
          siret: asksIdentity ? nullIfBlank(siret) : null,
          message: nullIfBlank(message),
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (422 === response.status && data.violations) {
          setFieldErrors(translateViolations(data.violations));
        } else {
          setError(translateApiMessage(data.error) || 'Votre demande n’a pas pu être envoyée.');
        }
        return false;
      }

      setClaim(data.data);
      return true;
    } catch {
      setError('Votre demande n’a pas pu être envoyée.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Take the pending request back, which frees the account to ask for
   * another listing or to write its own.
   */
  const withdraw = async () => {
    reset();
    setSaving(true);

    try {
      const response = await apiCall('/me/claim', { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Votre demande n’a pas pu être retirée.');
        return false;
      }

      setClaim(data.data);
      return true;
    } catch {
      setError('Votre demande n’a pas pu être retirée.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    claim,
    // The only state that changes what the account may do next.
    pending: 'PENDING' === claim?.status,
    loading,
    saving,
    error,
    fieldErrors,
    reset,
    send,
    withdraw,
  };
}
